import { useEffect, useState } from 'react';
import { useData } from '@yingbaba/api';
import type { DigitalProjectAssetView, Page, ProjectLicenseEvidence } from '@yingbaba/types';
import { Button, Empty, Modal, Notice, Pagination, QueryState } from '@yingbaba/ui';

export function licenseEvidence(a: DigitalProjectAssetView): ProjectLicenseEvidence {
  return {
    ...a,
    title: a.contract.title,
    license_name: a.contract.license.name,
    licensee_name: a.contract.licensee_name,
    project_name: a.contract.project_name,
    scope: a.contract.license.scope,
    restrictions: a.contract.license.restrictions,
    territory: a.contract.territory,
    asset_revision_id: a.contract.revision_id,
    contract_hash: '',
  };
}
export function BriefLicenseRefs({
  ids = [],
  evidence = [],
}: {
  ids?: string[];
  evidence?: ProjectLicenseEvidence[];
}) {
  if (!ids.length) return null;
  return (
    <div className="brief-license-refs">
      {ids.map((id) => {
        const v = evidence.find((v) => v.id === id);
        return (
          <div className="brief-license-evidence" key={id}>
            <div className="row spread wrap">
              <strong>{v?.title || '关联的素材许可'}</strong>
              <span className={`tag ${v?.available ? 'tag-success' : ''}`}>
                {v?.available ? '授权有效' : '需重新核对'}
              </span>
            </div>
            {v?.license_number && (
              <p className="small muted">
                {v.license_name} · {v.license_number}
              </p>
            )}
            {v?.licensee_name && <p className="small">被授权主体：{v.licensee_name}</p>}
            {!v?.available && (
              <Notice>
                {v?.unavailable_reason || '许可信息暂不可用，请核对项目中的授权素材。'}
              </Notice>
            )}
            {v && (
              <details>
                <summary>查看许可范围</summary>
                <p className="prose small">具名项目：{v.project_name}</p>
                <p className="prose small">{v.scope}</p>
                <p className="prose small">{v.restrictions}</p>
                <p className="small muted">地域：{v.territory === 'CN' ? '中国大陆' : '全球'}</p>
              </details>
            )}
          </div>
        );
      })}
    </div>
  );
}
export function BriefLicensePicker({
  projectID,
  open,
  value,
  onOpenChange,
  onConfirm,
}: {
  projectID: string;
  open: boolean;
  value: string[];
  onOpenChange: (open: boolean) => void;
  onConfirm: (ids: string[], evidence: ProjectLicenseEvidence[]) => void;
}) {
  const [page, setPage] = useState(1),
    [selected, setSelected] = useState<string[]>([]),
    [options, setOptions] = useState<DigitalProjectAssetView[]>([]);
  const q = useData<Page<DigitalProjectAssetView>>(
    `/projects/${projectID}/licensed-assets?page=${page}`,
    open,
  );
  useEffect(() => {
    if (open) {
      setSelected([...value]);
      setPage(1);
      setOptions([]);
    }
  }, [open]);
  useEffect(() => {
    if (open && q.data?.items)
      setOptions((old) => [...new Map([...old, ...q.data.items].map((v) => [v.id, v])).values()]);
  }, [q.data, open]);
  return (
    <Modal
      title="关联已购素材许可"
      description="从本项目的授权素材中选择对应许可。关联仅记录依据，不替代对成品用途、渠道和第三方权利的核对。"
      open={open}
      onOpenChange={onOpenChange}
      footer={
        <>
          <span className="dialog-footer-note small muted">已选 {selected.length} / 30 份许可</span>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button
            disabled={selected.length > 30 || q.isLoading || !!q.error}
            onClick={() => {
              onConfirm(selected, options.map(licenseEvidence));
              onOpenChange(false);
            }}
          >
            关联所选许可
          </Button>
        </>
      }
    >
      <QueryState query={q}>
        {!q.data?.items.length && (
          <Empty
            title="项目中还没有授权素材"
            description="先在项目素材页添加组织已购许可，再回来关联对应的权利类别。"
          />
        )}
        {q.data?.items.map((a) => (
          <article key={a.id} className="brief-license-option">
            <label className="project-tool-consent">
              <input
                type="checkbox"
                checked={selected.includes(a.id)}
                disabled={!a.available && !selected.includes(a.id)}
                onChange={(e) =>
                  setSelected((ids) =>
                    e.target.checked ? [...ids, a.id] : ids.filter((id) => id !== a.id),
                  )
                }
              />
              <span>
                <strong>{a.contract.title}</strong>
                <span className="small muted">
                  {a.contract.license.name} · {a.contract.licensee_name}
                </span>
              </span>
            </label>
            {!a.available && <p className="small muted">{a.unavailable_reason}</p>}
            <details>
              <summary>许可范围与限制</summary>
              <p>{a.contract.license.scope}</p>
              <p>{a.contract.license.restrictions}</p>
            </details>
          </article>
        ))}
      </QueryState>
      <Pagination page={page} size={24} total={q.data?.total || 0} onPage={setPage} />
    </Modal>
  );
}
